import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiDelete, apiPut } from '../api';
import Sidebar from '../components/Sidebar';

const POLL_MS = 5000;

const VERDICT_TONE = {
  constructive: 'bg-emerald-500/15 text-emerald-300 border-emerald-700/40',
  cautious: 'bg-amber-500/15 text-amber-300 border-amber-700/40',
  watch: 'bg-indigo-500/15 text-indigo-300 border-indigo-700/40',
};

const RUNNING_STATUSES = new Set(['pending', 'fetching_fundamentals', 'running', 'synthesizing']);

function fmtRelativeTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

function fmtUSD(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(Number(n));
}

export default function Research() {
  const [run, setRun] = useState(null);
  const [reports, setReports] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [notes, setNotes] = useState({}); // ticker → note string
  const [history, setHistory] = useState([]);
  const [openDetail, setOpenDetail] = useState(null); // {report data}
  const [openNoteFor, setOpenNoteFor] = useState(null); // ticker
  const [refreshingTickers, setRefreshingTickers] = useState(new Set());
  const [singleReportIds, setSingleReportIds] = useState({}); // ticker → reportId of an in-flight or recent single refresh
  const [err, setErr] = useState(null);
  const [busyStart, setBusyStart] = useState(false);
  const [showWatchlist, setShowWatchlist] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const isRunning = run && RUNNING_STATUSES.has(run.status);

  // Live cost: while running, reports[].cost_usd accumulates. After complete,
  // run.total_cost_usd is authoritative.
  const liveCost = useMemo(() => {
    if (run?.total_cost_usd != null) return Number(run.total_cost_usd);
    return reports.reduce((s, r) => s + (Number(r.cost_usd) || 0), 0);
  }, [run, reports]);

  const refreshRun = useCallback(async () => {
    try {
      const { run: r } = await apiGet('/api/research-runs/latest');
      setRun(r);
      if (r) {
        const { reports: childReports } = await apiGet(`/api/research-runs/${r.id}`);
        setReports(childReports || []);
      } else {
        setReports([]);
      }
    } catch (e) {
      // soft-fail; surface only if running
      if (isRunning) setErr(e.message);
    }
  }, [isRunning]);

  const refreshWatchlist = useCallback(async () => {
    try {
      const { tickers } = await apiGet('/api/watchlist');
      setWatchlist(tickers || []);
    } catch {
      /* ignore */
    }
  }, []);

  const refreshNotes = useCallback(async () => {
    try {
      const { notes: rows } = await apiGet('/api/research-notes');
      const map = {};
      for (const n of rows || []) map[n.ticker] = n.note;
      setNotes(map);
    } catch {
      /* ignore */
    }
  }, []);

  const refreshHistory = useCallback(async () => {
    try {
      const { runs } = await apiGet('/api/research-runs');
      setHistory(runs || []);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    refreshRun();
    refreshWatchlist();
    refreshNotes();
    refreshHistory();
  }, [refreshRun, refreshWatchlist, refreshNotes, refreshHistory]);

  // Poll while running.
  useEffect(() => {
    if (!isRunning) return undefined;
    const t = setInterval(refreshRun, POLL_MS);
    return () => clearInterval(t);
  }, [isRunning, refreshRun]);

  async function startRun() {
    setErr(null);
    setBusyStart(true);
    try {
      await apiPost('/api/research-runs');
      await refreshRun();
      await refreshHistory();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyStart(false);
    }
  }

  async function openReport(reportId) {
    try {
      const { report } = await apiGet(`/api/research/${reportId}`);
      setOpenDetail(report);
    } catch (e) {
      setErr(e.message);
    }
  }

  // Single-stock refresh: ~$0.50-1, ~1-2 min. Polls until ready, then merges
  // the new content into the displayed reports list.
  async function refreshStock(ticker) {
    setErr(null);
    setRefreshingTickers((s) => new Set(s).add(ticker));
    try {
      const { report } = await apiPost('/api/research-reports/refresh', { ticker });
      setSingleReportIds((m) => ({ ...m, [ticker]: report.id }));
      // Poll the new report
      const startedAt = Date.now();
      const MAX_MS = 5 * 60 * 1000;
      const POLL = 4000;
      while (Date.now() - startedAt < MAX_MS) {
        await new Promise((r) => setTimeout(r, POLL));
        const { report: r } = await apiGet(`/api/research/${report.id}`);
        if (r.status === 'ready' || r.status === 'failed') {
          // Merge into local reports list (replace existing card for this ticker)
          setReports((prev) => prev.map((p) =>
            p.ticker === ticker
              ? { ...p, status: r.status, model: r.model, cost_usd: r.cost_usd, error: r.error,
                  generated_at: r.generated_at, verdict: r.content_json?.verdict,
                  snapshot: r.content_json?.snapshot, _refreshed_id: r.id }
              : p,
          ));
          break;
        }
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setRefreshingTickers((s) => {
        const next = new Set(s);
        next.delete(ticker);
        return next;
      });
    }
  }

  async function saveNote(ticker, note) {
    try {
      await apiPut(`/api/research-notes/${encodeURIComponent(ticker)}`, { note });
      setNotes((m) => {
        const next = { ...m };
        if (note.trim()) next[ticker] = note.trim();
        else delete next[ticker];
        return next;
      });
      setOpenNoteFor(null);
    } catch (e) {
      setErr(e.message);
    }
  }

  async function loadHistoricalRun(runId) {
    try {
      const { run: r, reports: rs } = await apiGet(`/api/research-runs/${runId}`);
      setRun(r);
      setReports(rs || []);
      setShowHistory(false);
    } catch (e) {
      setErr(e.message);
    }
  }

  return (
    <div className="min-h-screen flex bg-neutral-950 text-neutral-100">
      <Sidebar />
      <main className="flex-1 px-6 py-10">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-2xl font-semibold mb-8">Deep research</h1>

        {/* Run banner */}
        <div className="rounded border border-neutral-800 bg-neutral-900 px-5 py-4 mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-lg font-medium mb-1">Portfolio research</h2>
              <p className="text-sm text-neutral-400">
                Per-stock AI agents with web search + fundamentals from EDGAR
                XBRL filings. Orchestrator synthesizes a portfolio-level take.
              </p>
            </div>
            <button
              type="button"
              onClick={startRun}
              disabled={busyStart || isRunning}
              className="shrink-0 px-4 py-2 rounded bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
            >
              {isRunning ? 'Running…' : busyStart ? 'Starting…' : 'Run research'}
            </button>
          </div>
          {run && (
            <div className="mt-4 text-sm">
              {isRunning ? (
                <RunProgress run={run} liveCost={liveCost} />
              ) : run.status === 'complete' ? (
                <div className="text-neutral-400">
                  Run #{run.id} · completed {fmtRelativeTime(run.completed_at)} · {run.total_count} stocks · cost {fmtUSD(run.total_cost_usd ?? liveCost)}
                </div>
              ) : run.status === 'failed' ? (
                <div className="text-red-400">Run #{run.id} failed.</div>
              ) : null}
            </div>
          )}
          {err && <div className="mt-3 text-sm text-red-400">{err}</div>}
        </div>

        {/* Run history */}
        <div className="rounded border border-neutral-800 bg-neutral-900 mb-6 overflow-hidden">
          <button
            type="button"
            onClick={() => setShowHistory((s) => !s)}
            className="w-full flex items-center justify-between px-5 py-3 hover:bg-neutral-900/70"
          >
            <span className="text-sm font-medium">Run history ({history.length})</span>
            <span className="text-neutral-400 text-sm">{showHistory ? '▾' : '▸'}</span>
          </button>
          {showHistory && (
            <div className="px-5 py-3 border-t border-neutral-800 space-y-1">
              {history.length === 0 ? (
                <p className="text-xs text-neutral-500">No runs yet.</p>
              ) : (
                history.map((h) => (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => loadHistoricalRun(h.id)}
                    className="w-full flex items-center justify-between text-left text-sm px-2 py-2 rounded hover:bg-neutral-950"
                  >
                    <span className="text-neutral-300">
                      Run #{h.id}
                      <span className="text-xs text-neutral-500 ml-2">
                        {h.completed_at
                          ? fmtRelativeTime(h.completed_at)
                          : h.started_at
                            ? `started ${fmtRelativeTime(h.started_at)}`
                            : '—'}
                      </span>
                    </span>
                    <span className="text-xs text-neutral-500">
                      {h.total_count} stocks · {h.status} · {fmtUSD(h.total_cost_usd)}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Watchlist */}
        <div className="rounded border border-neutral-800 bg-neutral-900 mb-6 overflow-hidden">
          <button
            type="button"
            onClick={() => setShowWatchlist((s) => !s)}
            className="w-full flex items-center justify-between px-5 py-3 hover:bg-neutral-900/70"
          >
            <span className="text-sm font-medium">Watchlist ({watchlist.length})</span>
            <span className="text-neutral-400 text-sm">{showWatchlist ? '▾' : '▸'}</span>
          </button>
          {showWatchlist && (
            <WatchlistSection watchlist={watchlist} onChange={refreshWatchlist} />
          )}
        </div>

        {/* Exec summary */}
        {run?.exec_summary && <ExecSummary summary={run.exec_summary} />}

        {/* Stock cards grid */}
        {reports.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            {reports.map((r) => (
              <StockCard
                key={r.id}
                report={r}
                note={notes[r.ticker]}
                refreshing={refreshingTickers.has(r.ticker)}
                onOpen={() => openReport(r._refreshed_id || r.id)}
                onRefresh={() => refreshStock(r.ticker)}
                onEditNote={() => setOpenNoteFor(r.ticker)}
              />
            ))}
          </div>
        )}

        {!run && !isRunning && (
          <div className="rounded border border-neutral-800 bg-neutral-900 px-6 py-8 text-center text-sm text-neutral-400">
            No research runs yet. Click <span className="text-neutral-200">Run research</span> to
            kick off your first portfolio analysis.
          </div>
        )}
      </div>

      {openDetail && (
        <DetailModal report={openDetail} onClose={() => setOpenDetail(null)} />
      )}

      {openNoteFor && (
        <NoteEditorModal
          ticker={openNoteFor}
          initial={notes[openNoteFor] || ''}
          onSave={(note) => saveNote(openNoteFor, note)}
          onClose={() => setOpenNoteFor(null)}
        />
      )}
      </main>
    </div>
  );
}

function RunProgress({ run, liveCost }) {
  const pct = run.total_count > 0 ? (run.complete_count / run.total_count) * 100 : 0;
  const phase =
    run.status === 'fetching_fundamentals'
      ? 'fetching fundamentals'
      : run.status === 'synthesizing'
        ? 'synthesizing portfolio summary'
        : 'researching stocks';
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-xs text-neutral-400">
        <span>
          Run #{run.id} · {phase}
          {liveCost > 0 && <span className="ml-2 text-neutral-500">· {fmtUSD(liveCost)} so far</span>}
        </span>
        <span>{run.complete_count} / {run.total_count} stocks</span>
      </div>
      <div className="w-full h-1.5 bg-neutral-800 rounded overflow-hidden">
        <div
          className="h-full bg-indigo-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function WatchlistSection({ watchlist, onChange }) {
  const [ticker, setTicker] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function add(e) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await apiPost('/api/watchlist', { ticker });
      setTicker('');
      await onChange();
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(t) {
    try {
      await apiDelete(`/api/watchlist/${encodeURIComponent(t)}`);
      await onChange();
    } catch (e) {
      setErr(e.message);
    }
  }

  return (
    <div className="px-5 py-4 border-t border-neutral-800 space-y-3">
      <form onSubmit={add} className="flex gap-2">
        <input
          type="text"
          maxLength={12}
          placeholder="Add ticker (e.g. NVDA)"
          value={ticker}
          onChange={(e) => setTicker(e.target.value.toUpperCase())}
          className="flex-1 px-3 py-1.5 rounded bg-neutral-950 border border-neutral-800 focus:border-indigo-500 outline-none text-sm uppercase"
        />
        <button
          type="submit"
          disabled={!ticker || busy}
          className="px-4 py-1.5 rounded bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white text-sm font-medium"
        >
          Add
        </button>
      </form>
      {err && <p className="text-xs text-red-400">{err}</p>}
      {watchlist.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {watchlist.map((w) => (
            <span
              key={w.id}
              className="inline-flex items-center gap-2 rounded bg-neutral-950 border border-neutral-800 px-2.5 py-1 text-xs"
            >
              <span className="text-neutral-100 font-mono">{w.ticker}</span>
              <button
                type="button"
                onClick={() => remove(w.ticker)}
                className="text-neutral-500 hover:text-red-400"
                aria-label={`Remove ${w.ticker}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-neutral-500">
          Watchlist tickers are researched alongside your held positions.
        </p>
      )}
    </div>
  );
}

function ExecSummary({ summary }) {
  return (
    <div className="rounded border border-neutral-800 bg-neutral-900 px-6 py-5 space-y-5">
      <div>
        <h3 className="text-sm uppercase tracking-wide text-neutral-500 mb-2">
          Executive summary
        </h3>
        <p className="text-neutral-200 leading-relaxed whitespace-pre-line">
          {summary.overall_take}
        </p>
      </div>

      {summary.actionable_items?.length > 0 && (
        <SummarySection title="Actionable items">
          {summary.actionable_items.map((it, i) => (
            <li key={i} className="flex gap-2">
              <span className="font-mono text-neutral-100 min-w-[64px]">{it.ticker}</span>
              <span className="text-neutral-300">
                <span className="text-neutral-400 uppercase text-xs mr-2">{it.action}</span>
                {it.reason}
              </span>
            </li>
          ))}
        </SummarySection>
      )}

      {summary.upcoming_events?.length > 0 && (
        <SummarySection title="Upcoming events">
          {summary.upcoming_events.map((it, i) => (
            <li key={i} className="flex gap-2">
              <span className="font-mono text-neutral-100 min-w-[64px]">{it.ticker}</span>
              <span className="text-neutral-300">
                {it.date && <span className="text-neutral-500 mr-2">{it.date}</span>}
                {it.event}
              </span>
            </li>
          ))}
        </SummarySection>
      )}

      {summary.top_risks?.length > 0 && (
        <SummarySection title="Top risks">
          {summary.top_risks.map((r, i) => (
            <li key={i}>
              <div className="font-medium text-neutral-200">
                {r.theme}
                {r.severity && (
                  <span className="ml-2 text-xs uppercase text-neutral-500">{r.severity}</span>
                )}
              </div>
              {r.affected_tickers && (
                <div className="text-xs text-neutral-500 font-mono">
                  {r.affected_tickers.join(', ')}
                </div>
              )}
              <div className="text-sm text-neutral-400 mt-0.5">{r.rationale}</div>
            </li>
          ))}
        </SummarySection>
      )}

      {summary.top_opportunities?.length > 0 && (
        <SummarySection title="Top opportunities">
          {summary.top_opportunities.map((o, i) => (
            <li key={i}>
              <div className="font-medium text-neutral-200">{o.theme}</div>
              {o.affected_tickers && (
                <div className="text-xs text-neutral-500 font-mono">
                  {o.affected_tickers.join(', ')}
                </div>
              )}
              <div className="text-sm text-neutral-400 mt-0.5">{o.rationale}</div>
            </li>
          ))}
        </SummarySection>
      )}

      {summary.watchlist_callouts?.length > 0 && (
        <SummarySection title="Watchlist callouts">
          {summary.watchlist_callouts.map((w, i) => (
            <li key={i} className="flex gap-2">
              <span className="font-mono text-neutral-100 min-w-[64px]">{w.ticker}</span>
              <span className="text-neutral-300">{w.reason}</span>
            </li>
          ))}
        </SummarySection>
      )}
    </div>
  );
}

function SummarySection({ title, children }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">{title}</h4>
      <ul className="space-y-2 text-sm">{children}</ul>
    </div>
  );
}

function StockCard({ report, note, refreshing, onOpen, onRefresh, onEditNote }) {
  const verdict = report.verdict || {};
  const snapshot = report.snapshot || {};
  const tone = VERDICT_TONE[verdict.tone] || 'bg-neutral-800 text-neutral-300 border-neutral-700';
  const isBusy = report.status !== 'ready' && report.status !== 'failed';
  const showSpinner = refreshing || isBusy;
  return (
    <div className="relative rounded border border-neutral-800 bg-neutral-900 hover:border-neutral-700">
      <button
        type="button"
        onClick={onOpen}
        disabled={report.status !== 'ready'}
        className="text-left w-full px-5 py-4 disabled:cursor-default"
      >
        <div className="flex items-start justify-between gap-3 mb-2 pr-14">
          <div>
            <div className="font-mono text-lg text-neutral-100">{report.ticker}</div>
            <div className="text-xs text-neutral-500">
              {snapshot.sector || '—'}
              {snapshot.holding_in_watchlist_only && (
                <span className="ml-2 text-indigo-400">• watchlist</span>
              )}
            </div>
          </div>
          {verdict.tone ? (
            <span className={`text-xs px-2 py-0.5 rounded border ${tone}`}>
              {verdict.tone}
            </span>
          ) : showSpinner ? (
            <span className="text-xs px-2 py-0.5 rounded border border-neutral-800 text-neutral-500">
              {refreshing ? 'refreshing…' : report.status}
            </span>
          ) : null}
        </div>
        <div className="text-sm text-neutral-300 line-clamp-2 pr-14">
          {verdict.one_liner ||
            (report.status === 'failed'
              ? report.error || 'failed'
              : showSpinner
                ? 'researching…'
                : '')}
        </div>
        {note && (
          <div className="mt-2 text-xs italic text-indigo-300/80 line-clamp-1 pr-14">
            ✎ {note}
          </div>
        )}
      </button>
      {/* Action buttons positioned over the card */}
      <div className="absolute top-3 right-3 flex gap-1">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEditNote();
          }}
          aria-label="Edit research note"
          className="p-1 rounded text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800"
          title="Research focus note"
        >
          ✎
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!refreshing) onRefresh();
          }}
          disabled={refreshing}
          aria-label="Refresh this stock"
          className="p-1 rounded text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 disabled:opacity-50"
          title="Re-run research for this ticker"
        >
          ↻
        </button>
      </div>
    </div>
  );
}

function NoteEditorModal({ ticker, initial, onSave, onClose }) {
  const [value, setValue] = useState(initial);
  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-neutral-950 border border-neutral-800 rounded-lg max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-neutral-800 flex justify-between items-center">
          <h3 className="text-sm font-medium">
            Research focus for <span className="font-mono">{ticker}</span>
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-100">✕</button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-neutral-500">
            What should the research agent prioritize for this ticker? This note is injected
            into the prompt on the next run. Up to 280 characters.
          </p>
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value.slice(0, 280))}
            rows={4}
            placeholder="e.g. Focus on AI capex outlook and competitive position vs NVDA."
            className="w-full px-3 py-2 rounded bg-neutral-900 border border-neutral-800 focus:border-indigo-500 outline-none text-sm"
          />
          <div className="flex justify-between items-center">
            <span className="text-xs text-neutral-500">{value.length}/280</span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-3 py-1.5 rounded text-neutral-400 hover:text-neutral-200 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => onSave(value)}
                className="px-3 py-1.5 rounded bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 3-level drill modal: top synthesis → 10 specialist cards → click a
// specialist to see its full structured content. Back button returns from
// the specialist view to the top synthesis.
function DetailModal({ report, onClose }) {
  const c = report.content_json;
  const [activeKind, setActiveKind] = useState(null);
  const specialists = report.specialist_reports || [];
  const summaries = c?.specialist_summaries || [];

  // Map kind → both summary entry and full specialist row (full content_json)
  const byKind = useMemo(() => {
    const map = {};
    for (const s of summaries) map[s.kind] = { ...map[s.kind], summary: s };
    for (const sr of specialists) map[sr.specialist_kind] = { ...map[sr.specialist_kind], full: sr };
    return map;
  }, [summaries, specialists]);

  const active = activeKind ? byKind[activeKind] : null;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-neutral-950 border border-neutral-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-neutral-950 border-b border-neutral-800 px-6 py-4 flex justify-between items-center z-10">
          <div className="flex items-center gap-3 min-w-0">
            {activeKind && (
              <button
                onClick={() => setActiveKind(null)}
                className="text-sm text-neutral-400 hover:text-neutral-100"
                aria-label="Back to summary"
              >
                ←
              </button>
            )}
            <h2 className="text-xl font-mono">{report.ticker}</h2>
            {activeKind && active?.summary && (
              <span className="text-sm text-neutral-400">· {active.summary.label}</span>
            )}
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-100">
            ✕
          </button>
        </div>

        {!activeKind ? (
          <SynthesisView c={c} report={report} byKind={byKind} onPickSpecialist={setActiveKind} />
        ) : (
          <SpecialistDeepDiveView entry={active} kind={activeKind} />
        )}
      </div>
    </div>
  );
}

function SynthesisView({ c, report, byKind, onPickSpecialist }) {
  if (!c) {
    return (
      <div className="px-6 py-5 text-sm text-neutral-500">
        {report.status}
        {report.error ? ` — ${report.error}` : ''}
      </div>
    );
  }
  // Order specialists in their original registry order so the layout is stable.
  const SPECIALIST_ORDER = [
    'fundamentals',
    'valuation',
    'news_events',
    'earnings_guidance',
    'analyst_view',
    'insider_activity',
    'capital_returns',
    'strategy_segments',
    'risks_catalysts',
    'macro_trends',
  ];
  const summaries = c.specialist_summaries || [];
  const orderedSummaries = SPECIALIST_ORDER
    .map((k) => summaries.find((s) => s.kind === k))
    .filter(Boolean);

  return (
    <div className="px-6 py-5 space-y-5 text-sm">
      {/* Verdict header */}
      {c.verdict && (
        <div className="flex items-start gap-3">
          <span className={`text-xs px-2 py-0.5 rounded border whitespace-nowrap ${VERDICT_TONE[c.verdict.tone] || 'border-neutral-700 text-neutral-300'}`}>
            {c.verdict.tone}
          </span>
          <div className="flex-1">
            {c.headline && (
              <p className="text-neutral-100 font-medium mb-1">{c.headline}</p>
            )}
            <p className="text-neutral-300">{c.verdict.one_liner}</p>
            {c.verdict.confidence && (
              <p className="text-xs text-neutral-500 mt-1">confidence: {c.verdict.confidence}</p>
            )}
          </div>
        </div>
      )}

      {c.position_context && (
        <Section title="Position context">
          <p className="text-neutral-300">{c.position_context}</p>
        </Section>
      )}

      {c.delta_vs_prior_run && (
        <Section title="Since last run">
          <p className="text-neutral-300">{c.delta_vs_prior_run}</p>
        </Section>
      )}

      <div>
        <h3 className="text-xs uppercase tracking-wide text-neutral-500 mb-3">
          Specialist summaries — click any for the full deep-dive
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {orderedSummaries.map((s) => {
            const tone = VERDICT_TONE[s.tone] || 'border-neutral-700 text-neutral-300';
            const ready = byKind[s.kind]?.full?.status === 'ready';
            return (
              <button
                key={s.kind}
                type="button"
                onClick={() => ready && onPickSpecialist(s.kind)}
                disabled={!ready}
                className="text-left rounded border border-neutral-800 bg-neutral-900 hover:border-neutral-700 p-3.5 disabled:cursor-default disabled:opacity-60"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="text-xs uppercase tracking-wide text-neutral-500">
                    {s.label}
                  </span>
                  {s.tone && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${tone}`}>
                      {s.tone}
                    </span>
                  )}
                </div>
                {s.headline && (
                  <p className="text-neutral-100 font-medium text-sm mb-1">{s.headline}</p>
                )}
                <p className="text-neutral-400 text-xs line-clamp-3">{s.summary}</p>
                {ready && (
                  <p className="mt-2 text-[11px] text-indigo-400">Open deep dive →</p>
                )}
                {!ready && (
                  <p className="mt-2 text-[11px] text-neutral-600">
                    {byKind[s.kind]?.full?.status === 'failed' ? 'failed' : 'in progress'}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SpecialistDeepDiveView({ entry, kind }) {
  const data = entry?.full?.content_json;
  if (!data) {
    return (
      <div className="px-6 py-5 text-sm text-neutral-500">
        Deep-dive content unavailable for this specialist.
      </div>
    );
  }
  return (
    <div className="px-6 py-5 space-y-4 text-sm">
      <p className="text-xs text-neutral-500">
        Specialist agent · {kind} · {entry.full?.model || ''}
      </p>
      <GenericRenderer data={data} />
    </div>
  );
}

// Walks an arbitrary content_json and renders it. Each top-level key becomes
// a labeled section. Strings → paragraphs, numbers → formatted, arrays of
// strings → bullets, nested objects → recurse.
function GenericRenderer({ data }) {
  return (
    <div className="space-y-4">
      {Object.entries(data || {}).map(([key, val]) => (
        <Field key={key} k={key} v={val} />
      ))}
    </div>
  );
}

function humanLabel(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bPe\b/g, 'P/E')
    .replace(/\bUsd\b/g, 'USD')
    .replace(/\bPct\b/g, '%')
    .replace(/\bMa\b/g, 'M&A');
}

function Field({ k, v }) {
  const label = humanLabel(k);
  if (v == null || v === '') return null;
  if (Array.isArray(v)) {
    if (v.length === 0) return null;
    return (
      <Section title={label}>
        <ul className="space-y-1 text-neutral-300">
          {v.map((item, i) => (
            <li key={i}>
              {typeof item === 'string' ? (
                <span>· {item}</span>
              ) : (
                <div className="rounded border border-neutral-800 bg-neutral-900/50 px-3 py-2">
                  <GenericRenderer data={item} />
                </div>
              )}
            </li>
          ))}
        </ul>
      </Section>
    );
  }
  if (typeof v === 'object') {
    return (
      <Section title={label}>
        <div className="rounded border border-neutral-800 bg-neutral-900/50 px-3 py-2">
          <GenericRenderer data={v} />
        </div>
      </Section>
    );
  }
  if (typeof v === 'number') {
    let formatted = v.toLocaleString();
    if (k.endsWith('_pct') || k.endsWith('_percent')) {
      formatted = `${(v * 100).toFixed(2)}%`;
    } else if (k.endsWith('_usd') || k.includes('price') || k.includes('cap') || k.includes('value')) {
      formatted = fmtUSD(v);
    }
    return (
      <div className="flex gap-3 text-sm">
        <span className="text-neutral-500 min-w-[140px]">{label}</span>
        <span className="text-neutral-200">{formatted}</span>
      </div>
    );
  }
  // String: short → inline, long → paragraph
  if (typeof v === 'string' && v.length < 80) {
    return (
      <div className="flex gap-3 text-sm">
        <span className="text-neutral-500 min-w-[140px]">{label}</span>
        <span className="text-neutral-200">{v}</span>
      </div>
    );
  }
  return (
    <Section title={label}>
      <p className="text-neutral-300 whitespace-pre-line">{v}</p>
    </Section>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">{title}</h3>
      {children}
    </div>
  );
}

function DefList({ rows }) {
  const filtered = rows.filter(([, v]) => v != null && v !== '');
  if (filtered.length === 0) return null;
  return (
    <dl className="space-y-1 text-sm">
      {filtered.map(([k, v]) => (
        <div key={k} className="flex gap-3">
          <dt className="text-neutral-500 min-w-[100px]">{k}</dt>
          <dd className="text-neutral-300">{String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}
